import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Book,
  Search,
  Download,
  ChevronLeft,
  Users,
  Bell,
  Briefcase,
  Activity,
  Building2,
  FileSearch,
  Settings,
  Shield,
  ShieldOff,
  Calendar,
  BarChart3,
  CheckCircle,
  Circle,
  ArrowRight,
  ExternalLink,
  Lightbulb,
  AlertTriangle,
  Clock,
  Eye,
  Plus,
  Pencil,
  Trash2,
  Play,
  Filter,
  ListChecks,
  HelpCircle,
  Home,
  Layout,
  Monitor,
  FolderOpen,
  UserCog,
  Lock,
  Unlock,
  RefreshCw,
  FileText,
  Send,
  CheckSquare,
  XCircle,
  TrendingUp,
  Network,
  MapPin,
  Zap
} from 'lucide-react';

// Componente Checklist reutilizable
const Checklist = ({ title, items, color = 'blue' }) => {
  const [checked, setChecked] = useState({});
  
  const toggleCheck = (index) => {
    setChecked(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const colorClasses = {
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    purple: 'border-purple-200 bg-purple-50',
    orange: 'border-orange-200 bg-orange-50',
    red: 'border-red-200 bg-red-50',
  };

  const checkColorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
  };

  return (
    <div className={`rounded-lg border-2 ${colorClasses[color]} p-4 my-4`}>
      <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
        <ListChecks size={18} className={checkColorClasses[color]} />
        {title}
      </h4>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div 
            key={`check-${item.substring(0, 30)}`}
            className="flex items-start gap-3 cursor-pointer hover:bg-white/50 p-2 rounded transition-colors"
            onClick={() => toggleCheck(index)}
          >
            {checked[index] ? (
              <CheckCircle size={20} className={`${checkColorClasses[color]} flex-shrink-0 mt-0.5`} />
            ) : (
              <Circle size={20} className="text-slate-300 flex-shrink-0 mt-0.5" />
            )}
            <span className={`text-sm ${checked[index] ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
              {item}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Componente Tip/Nota
const Tip = ({ children, type = 'info' }) => {
  const configs = {
    info: { icon: Lightbulb, bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800', iconColor: 'text-blue-500' },
    warning: { icon: AlertTriangle, bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', iconColor: 'text-amber-500' },
    success: { icon: CheckCircle, bg: 'bg-green-50 border-green-200', text: 'text-green-800', iconColor: 'text-green-500' },
    danger: { icon: XCircle, bg: 'bg-red-50 border-red-200', text: 'text-red-800', iconColor: 'text-red-500' },
  };
  const config = configs[type];
  const Icon = config.icon;

  return (
    <div className={`flex gap-3 p-4 rounded-lg border ${config.bg} my-4`}>
      <Icon size={20} className={`${config.iconColor} flex-shrink-0 mt-0.5`} />
      <div className={`text-sm ${config.text}`}>{children}</div>
    </div>
  );
};

// Componente para mostrar ruta de navegación
const NavPath = ({ path }) => (
  <div className="flex items-center gap-2 text-sm bg-slate-100 px-3 py-2 rounded-lg my-3 font-mono">
    <Home size={14} className="text-slate-500" />
    {path.split(' → ').map((item, idx, arr) => (
      <React.Fragment key={idx}>
        <span className={idx === arr.length - 1 ? 'text-blue-600 font-semibold' : 'text-slate-600'}>
          {item}
        </span>
        {idx < arr.length - 1 && <ChevronLeft size={14} className="text-slate-400 rotate-180" />}
      </React.Fragment>
    ))}
  </div>
);

// Componente Screenshot placeholder
const Screenshot = ({ alt, description }) => (
  <div className="my-4 border-2 border-dashed border-slate-300 rounded-lg p-8 bg-slate-50 text-center">
    <Monitor size={40} className="mx-auto text-slate-400 mb-2" />
    <p className="text-sm text-slate-500 font-medium">{alt}</p>
    {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
  </div>
);

// Secciones del manual
const SECTIONS = [
  { id: 'inicio', title: 'Inicio Rápido', icon: Zap, color: 'text-yellow-500' },
  { id: 'usuarios', title: 'Gestión de Usuarios', icon: Users, color: 'text-blue-500' },
  { id: 'alertas', title: 'Gestión de Alertas', icon: Bell, color: 'text-red-500' },
  { id: 'casos', title: 'Gestión de Casos', icon: Briefcase, color: 'text-purple-500' },
  { id: 'monitores', title: 'Configuración de Monitores', icon: Activity, color: 'text-green-500' },
  { id: 'empresas', title: 'Gestión de Empresas', icon: Building2, color: 'text-indigo-500' },
  { id: 'investigaciones', title: 'Investigaciones', icon: FileSearch, color: 'text-cyan-500' },
  { id: 'dashboard', title: 'Dashboard y Reportes', icon: BarChart3, color: 'text-orange-500' },
  { id: 'permisos', title: 'Control de Acceso', icon: Shield, color: 'text-violet-500' },
  { id: 'listas', title: 'Blacklist / Whitelist', icon: ShieldOff, color: 'text-rose-500' },
  { id: 'escalamiento', title: 'Escalamiento Automático', icon: TrendingUp, color: 'text-amber-500' },
  { id: 'programacion', title: 'Programación de Monitores', icon: Calendar, color: 'text-teal-500' },
  { id: 'sistema', title: 'Configuración del Sistema', icon: Settings, color: 'text-slate-500' },
];

const ManualUsuario = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('inicio');
  const contentRef = useRef(null);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDownloadPDF = () => {
    window.open('/MANUAL_USUARIO.pdf', '_blank');
  };

  const filteredSections = SECTIONS.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ChevronLeft size={20} />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Book size={24} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Manual de Usuario</h1>
                  <p className="text-sm text-slate-500">AlertGuard - Guía completa de uso</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar en el manual..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              <Button onClick={handleDownloadPDF} style={{ backgroundColor: '#2563eb', color: '#fff' }}>
                <Download size={18} className="mr-2" />
                Descargar PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Índice */}
          <div className="w-72 flex-shrink-0">
            <Card className="sticky top-24 p-4 bg-white max-h-[calc(100vh-120px)] overflow-y-auto">
              <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <FolderOpen size={18} />
                Contenido
              </h3>
              <nav className="space-y-1">
                {filteredSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon size={16} className={section.color} />
                      {section.title}
                    </button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Contenido Principal */}
          <div className="flex-1 min-w-0" ref={contentRef}>
            {/* SECCIÓN: INICIO RÁPIDO */}
            <section id="inicio" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Zap size={24} className="text-yellow-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Inicio Rápido</h2>
                    <p className="text-slate-500">Primeros pasos con AlertGuard</p>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-slate-800 mb-4">¿Qué es AlertGuard?</h3>
                <p className="text-slate-600 mb-4">
                  AlertGuard es un sistema integral de gestión de fraude y compliance diseñado para instituciones financieras.
                  Permite detectar transacciones sospechosas, gestionar alertas, investigar casos y generar reportes regulatorios.
                </p>

                <h3 className="text-lg font-semibold text-slate-800 mb-4 mt-8">Estructura de la Aplicación</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  {[
                    { icon: Activity, name: 'Monitoreo Transaccional', desc: 'Alertas, monitores, dashboard' },
                    { icon: Briefcase, name: 'Gestión de Casos', desc: 'Seguimiento de investigaciones' },
                    { icon: FileSearch, name: 'Investigaciones', desc: 'Análisis forense detallado' },
                    { icon: Building2, name: 'Clientes', desc: 'Padrón y empresas' },
                    { icon: Settings, name: 'Configuración', desc: 'Usuarios, permisos, sistema' },
                    { icon: Bell, name: 'Notificaciones', desc: 'Centro de alertas' },
                  ].map((mod, idx) => (
                    <div key={idx} className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors">
                      <mod.icon size={24} className="text-blue-600 mb-2" />
                      <h4 className="font-medium text-slate-900">{mod.name}</h4>
                      <p className="text-xs text-slate-500">{mod.desc}</p>
                    </div>
                  ))}
                </div>

                <Checklist
                  title="Checklist: Primeros pasos después del login"
                  color="green"
                  items={[
                    "Verificar que tu usuario tiene los permisos correctos",
                    "Revisar el Dashboard para ver el estado general",
                    "Explorar las alertas pendientes en Monitoreo → Alertas",
                    "Familiarizarte con los monitores activos en Monitoreo → Monitores",
                    "Configurar tus preferencias de notificaciones",
                  ]}
                />

                <Tip type="info">
                  <strong>Navegación rápida:</strong> Desde la página de inicio, hacé click en cualquier tarjeta de módulo para acceder directamente. 
                  El menú lateral aparece dentro de cada módulo con todas sus secciones.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: USUARIOS */}
            <section id="usuarios" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Gestión de Usuarios</h2>
                    <p className="text-slate-500">Crear, editar y administrar usuarios del sistema</p>
                  </div>
                </div>

                <NavPath path="Home → Configuración → Usuarios" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Roles del Sistema</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 px-4 py-2 text-left">Rol</th>
                        <th className="border border-slate-200 px-4 py-2 text-left">Descripción</th>
                        <th className="border border-slate-200 px-4 py-2 text-left">Permisos</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2">
                          <Badge className="bg-purple-100 text-purple-800">ADMINISTRADOR</Badge>
                        </td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Acceso total al sistema</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Todo: configuración, usuarios, reportes</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2">
                          <Badge className="bg-blue-100 text-blue-800">SUPERVISOR</Badge>
                        </td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Supervisor de equipo</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Aprobaciones, reportes, gestión de casos</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2">
                          <Badge className="bg-slate-100 text-slate-800">ANALISTA</Badge>
                        </td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Usuario operativo</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Alertas, casos, investigaciones básicas</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <Checklist
                  title="Checklist: Crear un nuevo usuario"
                  color="blue"
                  items={[
                    "Ir a Configuración → Usuarios",
                    "Click en el botón 'Nuevo Usuario'",
                    "Completar el email (será usado para login)",
                    "Ingresar el nombre completo",
                    "Crear una contraseña segura",
                    "Seleccionar el rol apropiado",
                    "Opcionalmente, asignar a grupos de trabajo",
                    "Click en 'Crear Usuario'",
                  ]}
                />

                <Checklist
                  title="Checklist: Cambiar contraseña de un usuario"
                  color="orange"
                  items={[
                    "Ir a Configuración → Usuarios",
                    "Buscar al usuario en la lista",
                    "Click en el icono de llave (🔑) en la fila del usuario",
                    "Ingresar la nueva contraseña",
                    "Confirmar la contraseña",
                    "Click en 'Cambiar Contraseña'",
                  ]}
                />

                <Tip type="warning">
                  <strong>Importante:</strong> Solo los usuarios con rol ADMINISTRADOR pueden crear, editar o eliminar otros usuarios.
                  Los cambios de contraseña no envían notificación automática al usuario.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: ALERTAS */}
            <section id="alertas" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Bell size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Gestión de Alertas</h2>
                    <p className="text-slate-500">Revisar, investigar y resolver alertas</p>
                  </div>
                </div>

                <NavPath path="Home → Monitoreo Transaccional → Alertas" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Estados de una Alerta</h3>
                <div className="flex flex-wrap gap-3 mb-6">
                  <Badge className="bg-slate-100 text-slate-800 px-3 py-1">NUEVA</Badge>
                  <ArrowRight size={16} className="text-slate-400 self-center" />
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">EN_REVISION</Badge>
                  <ArrowRight size={16} className="text-slate-400 self-center" />
                  <Badge className="bg-purple-100 text-purple-800 px-3 py-1">EN_INVESTIGACION</Badge>
                  <ArrowRight size={16} className="text-slate-400 self-center" />
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">CERRADA</Badge>
                </div>

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Severidades</h3>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="p-3 border-l-4 border-green-500 bg-green-50 rounded">
                    <p className="font-semibold text-green-800">BAJA</p>
                    <p className="text-xs text-green-600">Informativa</p>
                  </div>
                  <div className="p-3 border-l-4 border-yellow-500 bg-yellow-50 rounded">
                    <p className="font-semibold text-yellow-800">MEDIA</p>
                    <p className="text-xs text-yellow-600">Requiere revisión</p>
                  </div>
                  <div className="p-3 border-l-4 border-orange-500 bg-orange-50 rounded">
                    <p className="font-semibold text-orange-800">ALTA</p>
                    <p className="text-xs text-orange-600">Prioritaria</p>
                  </div>
                  <div className="p-3 border-l-4 border-red-500 bg-red-50 rounded">
                    <p className="font-semibold text-red-800">CRITICA</p>
                    <p className="text-xs text-red-600">Acción inmediata</p>
                  </div>
                </div>

                <Checklist
                  title="Checklist: Revisar y resolver una alerta"
                  color="red"
                  items={[
                    "Ir a Monitoreo Transaccional → Alertas",
                    "Usar los filtros para encontrar alertas pendientes",
                    "Click en una alerta para ver su detalle",
                    "Revisar la información de la transacción sospechosa",
                    "Analizar el cliente involucrado",
                    "Cambiar el estado a 'EN_REVISION'",
                    "Agregar un comentario con tu análisis",
                    "Decidir: Cerrar como falso positivo O Escalar/Crear caso",
                  ]}
                />

                <Checklist
                  title="Checklist: Crear un caso desde múltiples alertas"
                  color="purple"
                  items={[
                    "En la lista de alertas, marcar el checkbox de cada alerta relacionada",
                    "Aparecerá una barra con 'X alertas seleccionadas'",
                    "Click en 'Crear Caso con X Alertas'",
                    "Completar el título del caso",
                    "Agregar una descripción",
                    "Seleccionar el tipo de caso",
                    "Asignar prioridad",
                    "Click en 'Crear Caso'",
                  ]}
                />

                <Tip type="info">
                  <strong>Vista Kanban:</strong> Podés cambiar a vista Kanban para arrastrar y soltar alertas entre columnas de estado.
                  Usá los botones de vista en la esquina superior derecha.
                </Tip>

                <Tip type="warning">
                  <strong>Escalamiento automático:</strong> Las alertas de severidad ALTA o CRITICA que no se atiendan 
                  en 24 horas serán escaladas automáticamente (si está configurado).
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: CASOS */}
            <section id="casos" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Briefcase size={24} className="text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Gestión de Casos</h2>
                    <p className="text-slate-500">Seguimiento de investigaciones y workflow</p>
                  </div>
                </div>

                <NavPath path="Home → Gestión de Casos → Todos los Casos" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Flujo de un Caso</h3>
                <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-slate-50 rounded-lg">
                  <div className="px-3 py-2 bg-blue-100 text-blue-800 rounded font-medium text-sm">ABIERTO</div>
                  <ArrowRight size={16} className="text-slate-400" />
                  <div className="px-3 py-2 bg-yellow-100 text-yellow-800 rounded font-medium text-sm">EN_REVISION</div>
                  <ArrowRight size={16} className="text-slate-400" />
                  <div className="px-3 py-2 bg-orange-100 text-orange-800 rounded font-medium text-sm">PENDIENTE_APROBACION</div>
                  <ArrowRight size={16} className="text-slate-400" />
                  <div className="px-3 py-2 bg-green-100 text-green-800 rounded font-medium text-sm">CERRADO</div>
                  <ArrowRight size={16} className="text-slate-400" />
                  <div className="px-3 py-2 bg-slate-200 text-slate-700 rounded font-medium text-sm">ARCHIVADO</div>
                </div>

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Prioridades</h3>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-bold text-red-700">CRITICA</p>
                    <p className="text-xs text-red-600">Atención inmediata</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="font-bold text-orange-700">ALTA</p>
                    <p className="text-xs text-orange-600">Prioritario</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="font-bold text-yellow-700">MEDIA</p>
                    <p className="text-xs text-yellow-600">Normal</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-bold text-green-700">BAJA</p>
                    <p className="text-xs text-green-600">Sin urgencia</p>
                  </div>
                </div>

                <Checklist
                  title="Checklist: Trabajar en un caso asignado"
                  color="purple"
                  items={[
                    "Ir a Gestión de Casos → Mis Casos (para ver solo tus casos)",
                    "Abrir el caso haciendo click en él",
                    "Revisar la pestaña 'Alertas' para ver las alertas incluidas",
                    "Expandir cada alerta para ver sus transacciones",
                    "Resolver cada alerta individualmente (Lícita o Escalar)",
                    "Agregar comentarios documentando tu análisis",
                    "Cuando todas las alertas estén resueltas, cambiar estado a EN_REVISION",
                    "Si se requiere aprobación, cambiará a PENDIENTE_APROBACION",
                  ]}
                />

                <Checklist
                  title="Checklist: Resolver alertas dentro de un caso"
                  color="green"
                  items={[
                    "Abrir el detalle del caso",
                    "Ir a la pestaña 'Alertas'",
                    "Click en 'Resolver' en la alerta que querés resolver",
                    "Seleccionar: 'Lícita / Falso Positivo' o 'Escalar / Sospechosa'",
                    "Escribir un comentario justificando la decisión",
                    "Click en 'Guardar Resolución'",
                    "Repetir para cada alerta del caso",
                  ]}
                />

                <Tip type="success">
                  <strong>Dashboard personal:</strong> En "Mis Casos" podés ver tus métricas personales: 
                  casos abiertos, cerrados, tiempo promedio de resolución y más.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: MONITORES */}
            <section id="monitores" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Activity size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Configuración de Monitores</h2>
                    <p className="text-slate-500">Crear y configurar reglas de detección</p>
                  </div>
                </div>

                <NavPath path="Home → Monitoreo Transaccional → Monitores" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Tipos de Monitor</h3>
                <div className="space-y-3 mb-6">
                  <div className="p-4 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold text-slate-800">TRANSACCION_UNICA</h4>
                    <p className="text-sm text-slate-600">Detecta transacciones individuales que excedan un monto.</p>
                    <p className="text-xs text-slate-500 mt-1">Ejemplo: Transacciones mayores a $1,000,000</p>
                  </div>
                  <div className="p-4 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold text-slate-800">ACUMULACION_MONTO</h4>
                    <p className="text-sm text-slate-600">Suma de montos en un período de tiempo.</p>
                    <p className="text-xs text-slate-500 mt-1">Ejemplo: Acumulación diaria mayor a $5,000,000</p>
                  </div>
                  <div className="p-4 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold text-slate-800">ACUMULACION_CANTIDAD</h4>
                    <p className="text-sm text-slate-600">Número de operaciones en un período.</p>
                    <p className="text-xs text-slate-500 mt-1">Ejemplo: Más de 50 transacciones en un día</p>
                  </div>
                  <div className="p-4 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold text-slate-800">FRACCIONAMIENTO</h4>
                    <p className="text-sm text-slate-600">Detecta structuring (smurfing) - montos similares repetidos.</p>
                    <p className="text-xs text-slate-500 mt-1">Ejemplo: 5+ transacciones de montos similares en un día</p>
                  </div>
                </div>

                <Checklist
                  title="Checklist: Crear un nuevo monitor"
                  color="green"
                  items={[
                    "Ir a Monitoreo Transaccional → Monitores",
                    "Click en 'Nuevo Monitor'",
                    "Ingresar un nombre descriptivo",
                    "Escribir una descripción de qué detecta",
                    "Seleccionar la severidad de las alertas que generará",
                    "Elegir el tipo de monitor",
                    "Si es acumulación, seleccionar la ventana temporal (diario/semanal/mensual)",
                    "Configurar el umbral de detección",
                    "Definir los criterios (campo, operador, valor)",
                    "Activar el monitor",
                    "Click en 'Guardar'",
                  ]}
                />

                <Checklist
                  title="Checklist: Probar un monitor con Backtesting"
                  color="blue"
                  items={[
                    "En la lista de monitores, buscar el monitor a probar",
                    "Click en el botón 'Backtest' (icono de tubo de ensayo)",
                    "Seleccionar el rango de fechas a analizar",
                    "Opcionalmente, configurar filtros adicionales (monto mín/máx)",
                    "Click en 'Ejecutar Backtest'",
                    "Revisar los resultados (coincidencias encontradas)",
                    "Si querés crear alertas, seleccionar las coincidencias",
                    "Click en 'Crear Caso' si hay coincidencias relevantes",
                  ]}
                />

                <Tip type="info">
                  <strong>Monitores con Lógica Especial:</strong> Algunos monitores tienen lógica predefinida (como detección de smurfing).
                  Para estos, solo podés modificar el umbral usando el botón "Editar Umbral".
                </Tip>

                <Tip type="warning">
                  <strong>Cuidado con monitores muy sensibles:</strong> Un umbral muy bajo puede generar muchas alertas 
                  de falsos positivos. Usá el backtesting para calibrar antes de activar en producción.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: EMPRESAS */}
            <section id="empresas" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building2 size={24} className="text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Gestión de Empresas</h2>
                    <p className="text-slate-500">Alta, documentación y gestión de empresas</p>
                  </div>
                </div>

                <NavPath path="Home → Clientes → Empresas" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Estados de una Empresa</h3>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <Clock size={20} className="mx-auto text-yellow-600 mb-1" />
                    <p className="font-bold text-yellow-700">PENDIENTE</p>
                    <p className="text-xs text-yellow-600">En onboarding</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle size={20} className="mx-auto text-green-600 mb-1" />
                    <p className="font-bold text-green-700">ACTIVA</p>
                    <p className="text-xs text-green-600">Aprobada</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <AlertTriangle size={20} className="mx-auto text-orange-600 mb-1" />
                    <p className="font-bold text-orange-700">SUSPENDIDA</p>
                    <p className="text-xs text-orange-600">Temporalmente inactiva</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle size={20} className="mx-auto text-red-600 mb-1" />
                    <p className="font-bold text-red-700">RECHAZADA</p>
                    <p className="text-xs text-red-600">No aprobada</p>
                  </div>
                </div>

                <Checklist
                  title="Checklist: Dar de alta una nueva empresa"
                  color="indigo"
                  items={[
                    "Ir a Clientes → Empresas",
                    "Click en 'Nueva Empresa'",
                    "Completar Razón Social",
                    "Ingresar CUIT",
                    "Especificar la actividad principal",
                    "Completar domicilio legal",
                    "Ingresar datos del representante legal",
                    "Guardar y continuar a Documentación",
                    "Subir los documentos requeridos (marcados como obligatorios)",
                    "Una vez completo, la empresa queda en estado PENDIENTE para revisión",
                  ]}
                />

                <Checklist
                  title="Checklist: Revisar y aprobar una empresa"
                  color="green"
                  items={[
                    "Ir a Clientes → Empresas",
                    "Filtrar por estado 'PENDIENTE'",
                    "Abrir la empresa a revisar",
                    "Verificar que todos los datos estén correctos",
                    "Revisar los documentos adjuntos",
                    "Si todo está en orden, cambiar estado a 'ACTIVA'",
                    "Si falta información, agregar un comentario y mantener en PENDIENTE",
                    "Si no cumple requisitos, cambiar a 'RECHAZADA' con justificación",
                  ]}
                />

                <Tip type="info">
                  <strong>Documentos obligatorios:</strong> Los tipos de documento se configuran en 
                  Configuración → Sistema → Configuración de Empresas. Los marcados como obligatorios 
                  deben estar completos para aprobar la empresa.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: INVESTIGACIONES */}
            <section id="investigaciones" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-cyan-100 rounded-lg">
                    <FileSearch size={24} className="text-cyan-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Investigaciones</h2>
                    <p className="text-slate-500">Análisis forense detallado de clientes</p>
                  </div>
                </div>

                <NavPath path="Home → Investigaciones → Nueva Investigación" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">¿Qué es una Investigación?</h3>
                <p className="text-slate-600 mb-4">
                  Una investigación es un análisis profundo de uno o más clientes en un período de tiempo. 
                  El sistema analiza todas sus transacciones, detecta patrones sospechosos, identifica relaciones 
                  con otros clientes y genera un informe completo.
                </p>

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Pestañas de una Investigación</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                  {[
                    { name: 'Resumen', desc: 'Métricas principales y conclusiones' },
                    { name: 'Análisis Individual', desc: 'Detalle por cada cliente' },
                    { name: 'Cruces y Relaciones', desc: 'Transacciones entre investigados' },
                    { name: 'Timeline', desc: 'Línea temporal de eventos' },
                    { name: 'Red Global', desc: 'Visualización del grafo de conexiones' },
                    { name: 'Informe', desc: 'Generación de PDF' },
                  ].map((tab, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <p className="font-medium text-slate-800">{tab.name}</p>
                      <p className="text-xs text-slate-500">{tab.desc}</p>
                    </div>
                  ))}
                </div>

                <Checklist
                  title="Checklist: Crear una nueva investigación"
                  color="cyan"
                  items={[
                    "Ir a Investigaciones → Nueva Investigación",
                    "Ingresar el CUIT o CVU del cliente a investigar",
                    "Agregar más clientes si es necesario (click en 'Agregar')",
                    "Seleccionar el rango de fechas a analizar",
                    "Elegir el tipo de análisis (Individual o con Relaciones)",
                    "Opcionalmente, agregar un título descriptivo",
                    "Click en 'Iniciar Investigación'",
                    "Esperar a que se complete el análisis (puede tardar unos minutos)",
                  ]}
                />

                <Checklist
                  title="Checklist: Generar informe PDF de una investigación"
                  color="blue"
                  items={[
                    "Abrir la investigación completada",
                    "Ir a la pestaña 'Informe'",
                    "Revisar los datos que se incluirán",
                    "Opcionalmente, agregar comentarios adicionales",
                    "Click en 'Generar PDF'",
                    "El archivo se descargará automáticamente",
                  ]}
                />

                <Tip type="info">
                  <strong>Red Global:</strong> La pestaña "Red Global" muestra un grafo interactivo donde podés ver 
                  las conexiones entre clientes. Los nodos se colorean según su rol: Hub, Dispersor, Concentrador o Intermediario.
                </Tip>

                <Tip type="success">
                  <strong>Re-ejecutar análisis:</strong> Si necesitás actualizar los datos de una investigación existente, 
                  usá el botón "Re-ejecutar" para volver a correr el análisis con datos más recientes.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: DASHBOARD */}
            <section id="dashboard" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <BarChart3 size={24} className="text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Dashboard y Reportes</h2>
                    <p className="text-slate-500">Visualización de métricas y configuración</p>
                  </div>
                </div>

                <NavPath path="Home → Monitoreo Transaccional → Dashboard" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Widgets Disponibles</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                  {[
                    { name: 'Alertas Pendientes', desc: 'Contador por estado' },
                    { name: 'En Investigación', desc: 'Alertas activas' },
                    { name: 'Monitores Activos', desc: 'Total de monitores' },
                    { name: 'Alertas en el Tiempo', desc: 'Gráfico de evolución' },
                    { name: 'Por Severidad', desc: 'Distribución pie chart' },
                    { name: 'Por Monitor', desc: 'Top monitores' },
                    { name: 'Mapa de Calor', desc: 'Distribución geográfica' },
                    { name: 'Alertas Recientes', desc: 'Últimas alertas' },
                    { name: 'Última Ejecución', desc: 'Info de monitores' },
                  ].map((widget, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <p className="font-medium text-slate-800">{widget.name}</p>
                      <p className="text-xs text-slate-500">{widget.desc}</p>
                    </div>
                  ))}
                </div>

                <Checklist
                  title="Checklist: Personalizar el Dashboard"
                  color="orange"
                  items={[
                    "Ir a Configuración → Configuración de Dashboard",
                    "Ver la lista de widgets disponibles",
                    "Click en el ojo (👁) para mostrar/ocultar cada widget",
                    "Arrastrar los widgets para cambiar el orden",
                    "Configurar cuántas alertas recientes mostrar",
                    "Seleccionar el intervalo de actualización automática",
                    "Click en 'Guardar' para aplicar los cambios",
                  ]}
                />

                <Tip type="info">
                  <strong>Actualización automática:</strong> El dashboard puede actualizarse automáticamente 
                  cada 30 segundos, 1 minuto, 2 minutos o 5 minutos. También podés desactivarlo para actualización manual.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: PERMISOS */}
            <section id="permisos" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <Shield size={24} className="text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Control de Acceso</h2>
                    <p className="text-slate-500">Configurar permisos por rol o usuario</p>
                  </div>
                </div>

                <NavPath path="Home → Configuración → Permisos" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Tipos de Permiso</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 px-4 py-2 text-left">Permiso</th>
                        <th className="border border-slate-200 px-4 py-2 text-left">Descripción</th>
                        <th className="border border-slate-200 px-4 py-2 text-left">Ejemplo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2"><Eye size={16} className="inline mr-2" />Ver</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Puede ver y listar contenido</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Ver lista de alertas</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2"><Plus size={16} className="inline mr-2" />Crear</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Puede crear nuevos registros</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Crear un caso nuevo</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2"><Pencil size={16} className="inline mr-2" />Editar</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Puede modificar registros</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Cambiar estado de alerta</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2"><Trash2 size={16} className="inline mr-2" />Eliminar</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Puede eliminar registros</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Eliminar un monitor</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2"><Settings size={16} className="inline mr-2" />Configurar</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Puede cambiar configuraciones</td>
                        <td className="border border-slate-200 px-4 py-2 text-sm">Modificar umbrales</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <Checklist
                  title="Checklist: Configurar permisos para un rol"
                  color="violet"
                  items={[
                    "Ir a Configuración → Permisos",
                    "Seleccionar el módulo en el dropdown",
                    "Elegir 'Rol' como tipo de target",
                    "Seleccionar el rol a configurar (ej: ANALISTA)",
                    "Marcar los checkboxes de permisos para cada sección",
                    "Usar los botones rápidos (candado abierto/cerrado) para dar o quitar todos",
                    "Click en 'Guardar Permisos'",
                  ]}
                />

                <Tip type="warning">
                  <strong>Protección de rutas:</strong> Si un usuario intenta acceder a una URL sin permisos, 
                  será redirigido automáticamente. Los permisos se verifican tanto en el frontend como en el backend.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: LISTAS */}
            <section id="listas" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-rose-100 rounded-lg">
                    <ShieldOff size={24} className="text-rose-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Blacklist / Whitelist</h2>
                    <p className="text-slate-500">Gestionar listas de control de cuentas</p>
                  </div>
                </div>

                <NavPath path="Home → Monitoreo Transaccional → Listas de Control" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">¿Qué son las listas de control?</h3>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-bold text-red-800 flex items-center gap-2">
                      <ShieldOff size={18} /> BLACKLIST
                    </h4>
                    <p className="text-sm text-red-700 mt-2">
                      Cuentas bloqueadas o marcadas como sospechosas. Las transacciones con estas cuentas 
                      generarán alertas automáticas.
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-bold text-green-800 flex items-center gap-2">
                      <Shield size={18} /> WHITELIST
                    </h4>
                    <p className="text-sm text-green-700 mt-2">
                      Cuentas de confianza. Las transacciones con estas cuentas pueden ser excluidas 
                      de ciertos controles o monitores.
                    </p>
                  </div>
                </div>

                <Checklist
                  title="Checklist: Agregar una cuenta a Blacklist"
                  color="red"
                  items={[
                    "Ir a Monitoreo Transaccional → Listas de Control",
                    "Click en 'Agregar Cuenta'",
                    "Ingresar el identificador (CUIT, CVU o CBU)",
                    "Seleccionar 'BLACKLIST' como tipo",
                    "Escribir el motivo de la inclusión",
                    "Opcionalmente, establecer fecha de expiración",
                    "Click en 'Agregar'",
                  ]}
                />

                <Checklist
                  title="Checklist: Verificar si una cuenta está en alguna lista"
                  color="blue"
                  items={[
                    "Ir a Monitoreo Transaccional → Listas de Control",
                    "En el buscador 'Verificar Cuenta', ingresar el ID",
                    "Click en 'Verificar'",
                    "Ver el resultado: si está en blacklist, whitelist, o ninguna",
                  ]}
                />

                <Tip type="info">
                  <strong>Expiración:</strong> Podés configurar una fecha de expiración para que la entrada 
                  se elimine automáticamente. Dejá vacío para que sea permanente.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: ESCALAMIENTO */}
            <section id="escalamiento" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <TrendingUp size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Escalamiento Automático</h2>
                    <p className="text-slate-500">Configurar escalamiento de alertas no atendidas</p>
                  </div>
                </div>

                <NavPath path="Home → Monitoreo Transaccional → Escalamiento" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">¿Cómo funciona?</h3>
                <p className="text-slate-600 mb-4">
                  El escalamiento automático detecta alertas que llevan mucho tiempo sin ser atendidas 
                  y las escala automáticamente, cambiando su estado a ESCALADA y notificando a los supervisores.
                </p>

                <Checklist
                  title="Checklist: Configurar escalamiento automático"
                  color="amber"
                  items={[
                    "Ir a Monitoreo Transaccional → Escalamiento",
                    "Activar el switch 'Escalamiento Activo'",
                    "Configurar las horas límite (ej: 24 horas)",
                    "Seleccionar qué severidades se escalarán (ej: ALTA y CRITICA)",
                    "Click en 'Guardar'",
                    "Opcionalmente, ejecutar manualmente con 'Ejecutar Ahora' para probar",
                  ]}
                />

                <Tip type="warning">
                  <strong>Recomendación:</strong> Configurá el escalamiento solo para alertas de severidad ALTA y CRITICA.
                  Escalar todas las alertas puede generar sobrecarga en los supervisores.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: PROGRAMACIÓN */}
            <section id="programacion" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Calendar size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Programación de Monitores</h2>
                    <p className="text-slate-500">Configurar ejecución automática de monitores</p>
                  </div>
                </div>

                <NavPath path="Home → Monitoreo Transaccional → Programación" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Grupos de Monitores</h3>
                <p className="text-slate-600 mb-4">
                  Los monitores se organizan en grupos para su ejecución programada. Cada grupo puede 
                  tener su propia frecuencia de ejecución.
                </p>

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Frecuencias Disponibles</h3>
                <div className="grid grid-cols-5 gap-3 mb-6">
                  {[
                    { name: 'Manual', desc: 'Solo manual' },
                    { name: 'Cada hora', desc: '24 veces/día' },
                    { name: 'Cada 6h', desc: '4 veces/día' },
                    { name: 'Diario', desc: '1 vez/día' },
                    { name: 'Semanal', desc: '1 vez/semana' },
                  ].map((freq, idx) => (
                    <div key={idx} className="text-center p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <p className="font-medium text-slate-800">{freq.name}</p>
                      <p className="text-xs text-slate-500">{freq.desc}</p>
                    </div>
                  ))}
                </div>

                <Checklist
                  title="Checklist: Crear un grupo de monitores programado"
                  color="teal"
                  items={[
                    "Ir a Monitoreo Transaccional → Programación",
                    "Click en 'Nuevo Grupo'",
                    "Ingresar un nombre descriptivo (ej: 'Alta Prioridad - Diarios')",
                    "Seleccionar los monitores a incluir en el grupo",
                    "Elegir la frecuencia de ejecución",
                    "Activar el grupo",
                    "Click en 'Guardar'",
                  ]}
                />

                <Tip type="success">
                  <strong>Widget de última ejecución:</strong> En el Dashboard podés ver el widget 
                  "Última Ejecución de Monitores" que muestra cuándo se ejecutó el último grupo 
                  y cuántas alertas generó.
                </Tip>
              </Card>
            </section>

            {/* SECCIÓN: SISTEMA */}
            <section id="sistema" className="mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-slate-200 rounded-lg">
                    <Settings size={24} className="text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Configuración del Sistema</h2>
                    <p className="text-slate-500">Opciones avanzadas y limpieza de datos</p>
                  </div>
                </div>

                <NavPath path="Home → Configuración → Sistema" />

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Opciones Disponibles</h3>
                <div className="space-y-4 mb-6">
                  <div className="p-4 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold text-slate-800">Mostrar Credenciales de Prueba</h4>
                    <p className="text-sm text-slate-600">Muestra u oculta el cuadro de credenciales demo en el login.</p>
                  </div>
                  <div className="p-4 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold text-slate-800">Modo Producción</h4>
                    <p className="text-sm text-slate-600">Activa el modo producción y desactiva funciones de prueba.</p>
                  </div>
                  <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                    <h4 className="font-semibold text-red-800">Limpieza de Datos</h4>
                    <p className="text-sm text-red-700">Elimina alertas, casos o todos los datos de prueba. ¡Cuidado! Esta acción no se puede deshacer.</p>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-slate-800 mb-4">Tipos de Documento (Empresas)</h3>
                <p className="text-slate-600 mb-4">
                  Acá podés configurar qué documentos son requeridos durante el onboarding de empresas.
                  Los documentos marcados como "obligatorios" deben estar completos para aprobar una empresa.
                </p>

                <Checklist
                  title="Checklist: Agregar un nuevo tipo de documento"
                  color="purple"
                  items={[
                    "Ir a Configuración → Sistema",
                    "Buscar la sección 'Configuración de Empresas'",
                    "Click en 'Agregar Tipo de Documento'",
                    "Ingresar el código (ej: DNI_REPRESENTANTE)",
                    "Ingresar el nombre descriptivo",
                    "Seleccionar si es obligatorio u opcional",
                    "Click en 'Agregar'",
                  ]}
                />

                <h3 className="text-lg font-semibold text-slate-800 mb-4 mt-8">Tipos de Caso</h3>
                <p className="text-slate-600 mb-4">
                  Personalizá los tipos de caso disponibles. Podés agregar nuevos tipos con colores e iconos personalizados.
                </p>

                <Tip type="danger">
                  <strong>Zona de peligro:</strong> La limpieza de datos es irreversible. 
                  Usala solo cuando estés listo para pasar a producción y quieras eliminar todos los datos de prueba.
                </Tip>
              </Card>
            </section>

            {/* FINAL */}
            <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="text-center">
                <HelpCircle size={40} className="mx-auto text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">¿Necesitás más ayuda?</h3>
                <p className="text-slate-600 mb-4">
                  Si tenés dudas adicionales o encontrás algún problema, contactá al equipo de soporte técnico.
                </p>
                <Button onClick={handleDownloadPDF} variant="outline" className="border-blue-300 text-blue-700">
                  <Download size={18} className="mr-2" />
                  Descargar Manual en PDF
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualUsuario;
